import { useState, useEffect } from "react";
import { useRoute, Link } from "wouter";
import { Loader2, CheckCircle, Clock, AlertCircle } from "lucide-react";

export default function DataDeletion() {
  const [, params] = useRoute("/data-deletion/:code");
  const code = params?.code;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!code) return;
    fetch(`/api/meta/data-deletion/${code}`)
      .then(r => {
        if (!r.ok) throw new Error("Request not found");
        return r.json();
      })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [code]);

  const statusConfig: Record<string, { icon: any; color: string; label: string; description: string }> = {
    pending: { icon: Clock, color: "text-amber-400", label: "In Progress", description: "Your data deletion request is being processed. This may take up to 90 days." },
    completed: { icon: CheckCircle, color: "text-emerald-400", label: "Completed", description: "Your data has been successfully deleted from our systems." },
    failed: { icon: AlertCircle, color: "text-red-400", label: "Failed", description: "There was an issue processing your request. Please contact admin@lejjors.com for assistance." },
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-foreground" data-testid="data-deletion-page">
      <header className="border-b border-foreground/[0.06] px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <Link href="/">
            <span className="font-bold text-lg tracking-tight cursor-pointer">Drpr.ai</span>
          </Link>
        </div>
      </header>
      <main className="max-w-xl mx-auto px-6 py-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : error ? (
          <div className="text-center space-y-4">
            <AlertCircle className="mx-auto text-red-400" size={40} />
            <h1 className="text-xl font-bold">Request Not Found</h1>
            <p className="text-sm text-foreground/40">This deletion request could not be found. It may have already been processed or the code is invalid.</p>
          </div>
        ) : data ? (
          <div className="space-y-8">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold">Data Deletion Request</h1>
              <p className="text-sm text-foreground/40">Status of your data deletion request from Meta</p>
            </div>
            <div className="rounded-xl border border-foreground/[0.06] bg-foreground/[0.02] p-6 space-y-4">
              {(() => {
                const config = statusConfig[data.status] || statusConfig.pending;
                const Icon = config.icon;
                return (
                  <>
                    <div className="flex items-center gap-3">
                      <Icon className={config.color} size={24} />
                      <span className={`text-lg font-semibold ${config.color}`} data-testid="text-deletion-status">{config.label}</span>
                    </div>
                    <p className="text-sm text-foreground/50">{config.description}</p>
                  </>
                );
              })()}
              <div className="space-y-2 pt-2 border-t border-foreground/[0.04]">
                <div className="flex justify-between text-xs">
                  <span className="text-foreground/30">Confirmation Code</span>
                  <span className="text-foreground/60 font-mono" data-testid="text-confirmation-code">{data.confirmationCode}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-foreground/30">Requested</span>
                  <span className="text-foreground/60">{data.requestedAt ? new Date(data.requestedAt).toLocaleDateString() : "N/A"}</span>
                </div>
                {data.completedAt && (
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground/30">Completed</span>
                    <span className="text-foreground/60">{new Date(data.completedAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="text-center">
              <p className="text-xs text-foreground/20">For questions, contact <a href="mailto:admin@lejjors.com" className="text-blue-400/60 hover:text-blue-400">admin@lejjors.com</a></p>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}